import { useEffect, useState, useCallback } from "react";
import { getPeriods, addPeriod, deletePeriod } from "../api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Droplet, Trash2, TrendingUp, CalendarClock } from "lucide-react";
import { todayISO } from "../lib/planner";

const Stat = ({ label, value, suffix }) => (
  <div className="text-center">
    <p className="text-xs font-bold uppercase tracking-[0.12em] text-stone-400">{label}</p>
    <p className="font-heading text-2xl font-bold text-[#3b2a24] dark:text-stone-100 mt-1">
      {value ?? "—"}
      {value != null && suffix ? <span className="text-sm font-medium ml-0.5">{suffix}</span> : null}
    </p>
  </div>
);

export const CycleHistory = () => {
  const [stats, setStats] = useState(null);
  const [newDate, setNewDate] = useState(todayISO());
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => getPeriods().then(setStats), []);
  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!newDate) return;
    setBusy(true);
    try {
      const s = await addPeriod(newDate);
      setStats(s);
      toast.success("Period start recorded");
    } catch {
      toast.error("Could not record that date");
    }
    setBusy(false);
  };

  const remove = async (d) => {
    setBusy(true);
    try {
      setStats(await deletePeriod(d));
    } catch {
      toast.error("Could not remove that date");
    }
    setBusy(false);
  };

  if (!stats) return null;

  const dates = [...(stats.start_dates || [])].reverse();
  const lengthByStart = {};
  (stats.start_dates || []).forEach((d, i) => {
    if (i > 0) {
      const diff = Math.round(
        (new Date(d) - new Date(stats.start_dates[i - 1])) / 86400000
      );
      lengthByStart[d] = diff;
    }
  });

  return (
    <div
      data-testid="cycle-history-card"
      className="rounded-3xl bg-[#faf4ee] dark:bg-stone-900 border border-stone-100 dark:border-stone-800 p-6 sm:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] space-y-6"
    >
      <div className="flex items-center gap-2">
        <TrendingUp className="h-5 w-5 text-stone-400" />
        <h3 className="font-heading text-xl font-bold text-[#3b2a24] dark:text-stone-100">Cycle history & trends</h3>
      </div>

      {/* Trend stats */}
      <div className="grid grid-cols-3 gap-3 rounded-2xl bg-stone-50 dark:bg-stone-800/60 p-5">
        <Stat label="Avg length" value={stats.avg_cycle_length} suffix="d" />
        <Stat label="Shortest" value={stats.shortest} suffix="d" />
        <Stat label="Longest" value={stats.longest} suffix="d" />
      </div>

      {stats.next_predicted ? (
        <div className="flex items-center gap-3 rounded-2xl border border-rose-100 bg-rose-50 px-5 py-3" data-testid="next-period-predicted">
          <CalendarClock className="h-5 w-5 text-rose-500 shrink-0" />
          <p className="text-sm text-rose-800">
            Next period likely around <span className="font-semibold">{stats.next_predicted}</span>
            {stats.avg_cycle_length ? ` (learned ${stats.avg_cycle_length}-day average from ${stats.count} logged starts)` : ""}
          </p>
        </div>
      ) : (
        <p className="text-xs text-stone-400">
          Log at least two period start dates and the app will learn your real average cycle length.
        </p>
      )}

      {/* Add a past/new date */}
      <div>
        <Label className="text-sm text-stone-600 dark:text-stone-300">Add a period start date (past or today)</Label>
        <div className="flex gap-2 mt-2">
          <Input
            data-testid="new-period-date"
            type="date"
            value={newDate}
            max={todayISO()}
            onChange={(e) => setNewDate(e.target.value)}
            className="h-12 rounded-2xl border-stone-200"
          />
          <Button
            data-testid="add-period-btn"
            onClick={add}
            disabled={busy}
            className="h-12 px-5 rounded-full bg-[#b1543b] hover:bg-[#8f3f2b] shrink-0"
          >
            Add
          </Button>
        </div>
      </div>

      {/* Recorded dates */}
      {dates.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-[0.12em] text-stone-400">Recorded starts</p>
          {dates.map((d) => (
            <div
              key={d}
              data-testid={`period-row-${d}`}
              className="flex items-center justify-between py-2.5 px-3 rounded-2xl hover:bg-stone-50 dark:hover:bg-stone-800"
            >
              <div className="flex items-center gap-3">
                <Droplet className="h-4 w-4 text-rose-400" />
                <span className="text-base text-[#3b2a24] dark:text-stone-100">{d}</span>
                {lengthByStart[d] && (
                  <span className="text-xs text-stone-400">{lengthByStart[d]}d cycle</span>
                )}
              </div>
              <button
                type="button"
                data-testid={`delete-period-${d}`}
                onClick={() => remove(d)}
                className="text-stone-300 hover:text-rose-500 transition-colors"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
